/**
 * Kundenstammverwaltung.
 *
 * Eine Liste für Personen und Organisationen, sortier- und durchsuchbar.
 * Aufgeklappt zeigt jede Zeile die Angebote des Kunden – pausierte Vorgänge
 * tragen dort dieselbe gelbe Kennzeichnung wie im Cockpit.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { KundenModal } from '../modals/KundenModal';
import { dat, norm } from '../hilfen/format';
import type { Angebot, Kunde } from '../typen';
import './kunden.css';

const AKTIVE_STATUS = ['START', 'in Arbeit', 'Gesendet', 'Rückfrage', 'Freigabe Technik'];

type SortSpalte = 'typ' | 'nr' | 'name' | 'ort' | 'angebote';

export function Kundenstamm() {
  const navigate = useNavigate();
  const { darf } = useKora();

  const [suche, setSuche] = useState('');
  const [rolle, setRolle] = useState<'Alle' | 'Kunden' | 'Lieferanten'>('Alle');
  const [sortSpalte, setSortSpalte] = useState<SortSpalte>('nr');
  const [richtung, setRichtung] = useState<'asc' | 'desc'>('desc');
  const [offenerKunde, setOffenerKunde] = useState<number | null>(null);
  const [modal, setModal] = useState<{ kundeId: number | null } | null>(null);

  const kundenAbfrage = useQuery({
    queryKey: ['kunden'],
    queryFn: () => api.hole<Kunde[]>('/kunden'),
  });

  const angeboteAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
    enabled: darf('monitor'),
  });

  const kunden = kundenAbfrage.data ?? [];
  const angebote = useMemo(() => angeboteAbfrage.data ?? [], [angeboteAbfrage.data]);

  const angeboteJeKunde = useMemo(() => {
    const karte = new Map<number, Angebot[]>();
    for (const a of angebote) {
      const liste = karte.get(a.kunde.id) ?? [];
      liste.push(a);
      karte.set(a.kunde.id, liste);
    }
    for (const liste of karte.values()) liste.sort((x, y) => y.nr - x.nr);
    return karte;
  }, [angebote]);

  const q = norm(suche);

  const gefiltert = useMemo(
    () =>
      kunden
        .filter((k) => rolle === 'Alle' || rolle.indexOf(k.rolle) === 0)
        .filter(
          (k) =>
            !q ||
            [k.name, k.ort, k.plz, k.mail, k.telefon, k.adresse, k.kundenNr].some((x) =>
              norm(x).includes(q),
            ),
        ),
    [kunden, rolle, q],
  );

  const sortiert = useMemo(() => {
    const faktor = richtung === 'desc' ? -1 : 1;
    const wert = (k: Kunde): string | number => {
      switch (sortSpalte) {
        case 'typ':
          return (k.typ === 'organisation' ? '0' : '1') + norm(k.name);
        case 'nr':
          return Number(String(k.kundenNr).replace(/\D/g, '')) || 0;
        case 'name':
          return norm(k.name);
        case 'ort':
          return norm(k.ortZeile);
        case 'angebote':
          return angeboteJeKunde.get(k.id)?.length ?? 0;
      }
    };
    return [...gefiltert].sort((a, b) => {
      const fa = wert(a);
      const fb = wert(b);
      return (fa > fb ? 1 : fa < fb ? -1 : 0) * faktor;
    });
  }, [gefiltert, sortSpalte, richtung, angeboteJeKunde]);

  function sortieren(spalte: SortSpalte) {
    if (spalte === sortSpalte) setRichtung((r) => (r === 'asc' ? 'desc' : 'asc'));
    else {
      setSortSpalte(spalte);
      setRichtung('asc');
    }
  }

  const rollenTabs: { label: 'Alle' | 'Kunden' | 'Lieferanten'; zahl: number }[] = [
    { label: 'Alle', zahl: kunden.length },
    { label: 'Kunden', zahl: kunden.filter((k) => k.rolle === 'Kunde').length },
    { label: 'Lieferanten', zahl: kunden.filter((k) => k.rolle === 'Lieferant').length },
  ];

  const spalten: { key: SortSpalte | 'kontakt'; label: string; fest?: boolean }[] = [
    { key: 'typ', label: 'Typ' },
    { key: 'nr', label: 'Knd.-Nr' },
    { key: 'name', label: 'Name' },
    { key: 'ort', label: 'Ort' },
    { key: 'kontakt', label: 'Kontakt', fest: true },
    { key: 'angebote', label: 'Angebote' },
  ];

  return (
    <div className="eh-inhalt">
      <div className="eh-kopfzeile">
        <div>
          <div className="eh-kicker">Stammdaten</div>
          <div className="eh-titel">Kundenstammverwaltung</div>
          <div className="kunden-untertitel">
            {gefiltert.length} von {kunden.length} Kontakten · Angebote je Kunde gebündelt
          </div>
        </div>
        <div className="kunden-aktionen">
          <input
            className="kunden-suche"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Name, Ort, Mail, Telefon …"
          />
          <button
            type="button"
            className="kunden-neu"
            onClick={() => setModal({ kundeId: null })}
          >
            <span>Neuen Kunden anlegen</span>
            <span style={{ fontSize: 17, lineHeight: 1 }}>+</span>
          </button>
        </div>
      </div>

      <div className="kunden-tabs">
        <div className="kunden-tabs-liste">
          {rollenTabs.map((r) => (
            <button
              key={r.label}
              type="button"
              className={`kunden-tab ${rolle === r.label ? 'aktiv' : ''}`}
              onClick={() => {
                setRolle(r.label);
                setOffenerKunde(null);
              }}
            >
              <span>{r.label}</span>
              <span className="kunden-tab-zahl">{r.zahl}</span>
            </button>
          ))}
        </div>
        <div className="kunden-legende">
          <span>
            <span className="kunden-symbol-person">
              <span />
            </span>
            Privatperson
          </span>
          <span>
            <span className="kunden-symbol-org">
              <span />
              <span />
              <span />
              <span />
            </span>
            Organisation
          </span>
        </div>
      </div>

      <div className="kunden-scroll">
        <div className="kunden-breite">
          <div className="kunden-zeile kunden-kopf">
            {spalten.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`kunden-sortknopf ${sortSpalte === c.key ? 'aktiv' : ''}`}
                style={{ cursor: c.fest ? 'default' : 'pointer' }}
                onClick={() => !c.fest && sortieren(c.key as SortSpalte)}
              >
                <span>{c.label}</span>
                <span className="kunden-pfeil">
                  {sortSpalte === c.key ? (richtung === 'desc' ? '▼' : '▲') : ''}
                </span>
              </button>
            ))}
            <div />
          </div>

          <div className="kunden-liste">
            {sortiert.map((k) => {
              const eigene = angeboteJeKunde.get(k.id) ?? [];
              const mehrfach = eigene.length > 1;
              const offen = offenerKunde === k.id;

              const hinweis =
                (mehrfach
                  ? `Mehrfachkunde: ${eigene.map((a) => a.prefix).join(' + ')} – Angebote laufen getrennt, Kundendaten sind geteilt.`
                  : eigene.length === 1
                    ? 'Ein laufender Vorgang.'
                    : 'Noch kein Angebot zu diesem Kontakt.') +
                (k.tags.length ? ` · Merkmale: ${k.tags.join(', ')}` : '') +
                (k.notiz ? ` · ${k.notiz}` : '');

              return (
                <div
                  className="kunden-karte"
                  key={k.id}
                  style={{ borderColor: mehrfach ? '#E3D2CD' : 'var(--eh-rahmen)' }}
                >
                  <div
                    className="kunden-zeile kunden-datenzeile"
                    onClick={() => setOffenerKunde(offen ? null : k.id)}
                  >
                    <span style={{ display: 'flex' }}>
                      {k.typ === 'organisation' ? (
                        <span className="kunden-symbol-org gross" title="Organisation">
                          <span />
                          <span />
                          <span />
                          <span />
                        </span>
                      ) : (
                        <span className="kunden-symbol-person gross" title="Privatperson">
                          <span />
                        </span>
                      )}
                    </span>

                    <span className="kunden-nr">{k.kundenNr}</span>

                    <span className="kunden-namensblock">
                      <span className="kunden-name">{k.name}</span>
                      <span className="kunden-adresse">
                        {k.rolle} · {k.adresse}
                      </span>
                    </span>

                    <span className="kunden-ort">{k.ortZeile}</span>

                    <span className="kunden-kontakt">
                      <span className="kunden-mail">{k.mail || '—'}</span>
                      <span className="kunden-telefon">{k.telefon || '—'}</span>
                    </span>

                    <span className="kunden-angebote">
                      <span className="kunden-anzahl">
                        {eigene.length === 1 ? '1 Angebot' : `${eigene.length} Angebote`}
                      </span>
                      <span className="kunden-tags">
                        {eigene.map((a) => {
                          const gelb = a.pausiert || a.wvFaellig;
                          const label = a.pausiert
                            ? `${a.prefix} · pausiert bis ${dat(a.wiedervorlage)}`
                            : a.wvFaellig
                              ? `${a.prefix} · Wiedervorlage fällig`
                              : a.prefix;
                          const aktiv = AKTIVE_STATUS.includes(a.status);
                          return (
                            <span
                              key={a.id}
                              className="kunden-tag"
                              style={{
                                background: gelb
                                  ? 'var(--eh-gelb-flaeche)'
                                  : aktiv
                                    ? 'var(--eh-rot-flaeche)'
                                    : 'var(--eh-avatar)',
                                color: gelb
                                  ? 'var(--eh-gelb-text)'
                                  : aktiv
                                    ? 'var(--eh-rot)'
                                    : 'var(--eh-text-4)',
                              }}
                            >
                              {label}
                            </span>
                          );
                        })}
                      </span>
                    </span>

                    <span className="kunden-werkzeuge">
                      <button
                        type="button"
                        className="kunden-stift"
                        title="Kontaktdaten bearbeiten"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModal({ kundeId: k.id });
                        }}
                      >
                        ✎
                      </button>
                      <span className="kunden-chevron">{offen ? '▲' : '▼'}</span>
                    </span>
                  </div>

                  {offen && (
                    <div className="kunden-aufklappung">
                      {eigene.map((a) => {
                        const gelb = a.pausiert || a.wvFaellig;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            className="kunden-angebotszeile"
                            onClick={() => navigate(`/angebot/${a.id}`)}
                          >
                            <span className="kunden-angebot-name">{a.name}</span>
                            <span className="kunden-angebot-kategorie">{a.kategorie}</span>
                            <span>
                              <span
                                className="kunden-angebot-status"
                                style={{
                                  background: gelb
                                    ? 'var(--eh-gelb-flaeche)'
                                    : 'var(--eh-neutral-flaeche)',
                                  color: gelb ? 'var(--eh-gelb-text)' : 'var(--eh-text-2)',
                                }}
                              >
                                {a.pausiert
                                  ? `Pausiert bis ${dat(a.wiedervorlage)}`
                                  : a.wvFaellig
                                    ? 'Wiedervorlage fällig'
                                    : a.status}
                              </span>
                            </span>
                            <span
                              className="kunden-angebot-alter"
                              style={{ color: a.ampel.fg }}
                            >
                              {a.alterText}
                            </span>
                          </button>
                        );
                      })}
                      <div className="kunden-hinweis">{hinweis}</div>
                    </div>
                  )}
                </div>
              );
            })}

            {!kundenAbfrage.isLoading && sortiert.length === 0 && (
              <div className="eh-leer">Kein Kontakt gefunden.</div>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <KundenModal
          kundeId={modal.kundeId}
          onSchliessen={() => setModal(null)}
        />
      )}
    </div>
  );
}
